import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { ToastrService } from 'ngx-toastr';

import { AuthService } from '../../providers/auth/auth.service';
import { ApiProvider } from '../../providers/api/api';
import { HelpersProvider } from '../../providers/helpers/helpers';
import { I18nProvider } from '../../providers/i18n/i18n';

@Component({
  selector: 'app-indicacoes',
  templateUrl: './indicacoes.component.html',
})
export class IndicacoesComponent implements OnDestroy, OnInit {
  private readonly indicationsBodyClass = 'indicacoes-page-active';

  PAGE: any;
  STRINGS: any;
  user: any = {};
  referralLink: string = '';

  reports: any[] = [];
  pageSize: number = 10;
  pageIndex: number = 0;
  length: number = 0;
  loading: boolean = false;
  savingPix: boolean = false;
  openFaqIndex: number | null = 0;

  pixForm = new FormGroup({
    pix_key: new FormControl('', [Validators.required]),
    bank: new FormControl(''),
    holder_name: new FormControl('', [Validators.required]),
  });

  @ViewChild('paginator') paginator: MatPaginator;

  constructor(
    public auth: AuthService,
    public api: ApiProvider,
    public helpers: HelpersProvider,
    public i18n: I18nProvider,
    public toastr: ToastrService,
  ) {
    this.STRINGS = this.i18n.translate();
    this.PAGE = this.i18n.translate('IndicationsPage');
  }

  ngOnInit() {
    document.body.classList.add(this.indicationsBodyClass);
    this.user = this.auth.retrieveLocalUser();
    this.referralLink = this.buildReferralLink();
    this.loadReports();
  }

  ngOnDestroy() {
    document.body.classList.remove(this.indicationsBodyClass);
  }

  buildReferralLink() {
    const code = this.user && this.user.link ? this.user.link : 'usercodeXYZ';
    return window.location.origin + '/professor/nova-conta?ref=' + code;
  }

  copyReferralLink() {
    if (this.helpers.copyTextToClipboard(this.referralLink)) {
      this.toastr.success(
        this.PAGE.link_copied || 'Link copiado para a área de transferência.',
        '',
        this.helpers.toastrCfgs
      );
      return;
    }

    this.toastr.error(
      this.PAGE.link_copy_error || 'Não foi possível copiar o link. Copie-o manualmente.',
      '',
      this.helpers.toastrCfgs
    );
  }

  toggleFaq(index: number) {
    this.openFaqIndex = this.openFaqIndex === index ? null : index;
  }

  reportsPage() {
    return this.reports;
  }

  changePage(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.loadReports();
  }

  loadReports() {
    this.loading = true;
    this.api.get('v3/referral/teacher', {
      params: {
        page: this.pageIndex + 1,
        per_page: this.pageSize,
      }
    }).then((res: any) => {
      const data = res && res.reports ? res.reports : {};
      this.reports = (data.data || []).map((row: any) => this.normalizeReport(row));
      this.length = data.total || 0;
      if (res && res.link_code) {
        this.referralLink = window.location.origin + '/professor/nova-conta?ref=' + res.link_code;
      }
      this.fillPayoutAccount(res ? res.payout_account : null);
      this.loading = false;
    }).catch((_err: any) => {
      this.reports = [];
      this.length = 0;
      this.loading = false;
    });
  }

  fillPayoutAccount(account: any) {
    if (!account) return;
    this.pixForm.patchValue({
      pix_key: account.pix_key || '',
      bank: account.bank || '',
      holder_name: account.holder_name || '',
    });
  }

  savePayoutAccount() {
    if (this.pixForm.invalid) {
      this.validateAllFormFields(this.pixForm);
      return false;
    }

    this.savingPix = true;
    this.api.put('v3/referral/teacher/payout-account', this.pixForm.value).then((_res: any) => {
      this.savingPix = false;
      this.toastr.success(this.PAGE.pix_saved || 'Dados salvos com sucesso.', '', this.helpers.toastrCfgs);
    }).catch((_err: any) => {
      this.savingPix = false;
      this.toastr.error(this.PAGE.pix_save_error || 'Nao foi possivel salvar os dados.', '', this.helpers.toastrCfgs);
    });
  }

  validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  normalizeReport(row: any) {
    const paymentExpected = this.isPaymentExpected(row);
    return {
      date: this.formatDate(row.date),
      email: row.email || '-',
      status: row.premium ? this.PAGE.status_premium : this.PAGE.status_free,
      commission: row.premium ? this.formatCurrency(row.commission_amount_cents, row.commission_currency) : this.formatCurrency(0, row.commission_currency),
      paymentStatus: this.formatPaymentStatus(row),
      premium: row.premium,
      paymentExpected,
    };
  }

  isPaymentExpected(row: any) {
    return row && row.premium && row.payment_date && row.bonus_status !== 'paid';
  }

  formatPaymentStatus(row: any) {
    if (!row || !row.premium || !row.payment_date) {
      return '-';
    }
    const date = this.formatDate(row.payment_date);
    if (row.bonus_status === 'paid') {
      return (this.PAGE.payment_paid_prefix || 'Pago em') + ' ' + date;
    }
    return (this.PAGE.payment_expected_prefix || 'Previsto para') + ' ' + date;
  }

  formatCurrency(amountCents: number, currency: string = 'BRL') {
    const value = (amountCents || 0) / 100;
    return value.toLocaleString('pt-BR', { style: 'currency', currency: currency || 'BRL' });
  }

  formatDate(date: string) {
    if (!date) return '-';
    const parts = date.substring(0, 10).split('-');
    if (parts.length !== 3) return date;
    return parts[2] + '/' + parts[1] + '/' + parts[0];
  }
}
